/**
 * IMPORTS
 */

import { useEffect, useState } from "react";

type TipoEntrega = {
  id: number;
  tipo: string;
  endereco_entrega_id?: number | null;
};

type Props = {
  tiposEntrega: TipoEntrega[];
  tipoEntregaSelecionada: string; // ex: "motoboy_letscom"
  onTipoEntregaChange: (tipo: TipoEntrega) => void;
  outrosTexto?: string;
  onOutrosChange?: (texto: string) => void;
  outrosSomenteLeitura?: boolean;
};

const formatarTipo = (tipo: string) => {
  const nomes = {
    balcao: "Balcão",
    correios: "Correios",
    motoboy_letscom: "Motoboy Letscom",
    transportadora: "Transportadora",
    outros: "Outros",
  };
  return nomes[tipo as keyof typeof nomes] || tipo;
};

const DeliveryOptions = ({
  tiposEntrega,
  tipoEntregaSelecionada,
  onTipoEntregaChange,
  outrosTexto,
  onOutrosChange,
  outrosSomenteLeitura = false,
}: Props) => {
  const [selectedOption, setSelectedOption] = useState(tipoEntregaSelecionada);
  const [otherOption, setOtherOption] = useState<string>(outrosTexto ?? "");

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id: tipoSelecionado } = event.target;

    setSelectedOption(tipoSelecionado);

    const tipoSelecionadoObj = tiposEntrega.find((item) => item.tipo === tipoSelecionado);

    if (tipoSelecionadoObj) {
      onTipoEntregaChange({
        id: tipoSelecionadoObj.id,
        tipo: tipoSelecionadoObj.tipo,
        endereco_entrega_id: null,
      });

      if (tipoSelecionadoObj.tipo !== "outros") {
        setOtherOption("");
        onOutrosChange?.("");
      }
    }
  };

  const handleOtherOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setOtherOption(value);
    onOutrosChange?.(value);
  };

  // ✅ sempre que a prop tipoEntregaSelecionada mudar, atualiza o state interno
  useEffect(() => {
    setSelectedOption(tipoEntregaSelecionada);
  }, [tipoEntregaSelecionada]);

  useEffect(() => {
    if (typeof outrosTexto === "string") {
      setOtherOption(outrosTexto);
    }
  }, [outrosTexto]);

  const textoOutrosExibido = (outrosTexto ?? otherOption).trim();
  const exibirOutrosSomenteLeitura = outrosSomenteLeitura && textoOutrosExibido.length > 0;

  return (
    <div className="rounded-md bg-gray-50 p-4 shadow-md">
      <h3 className="mb-4 text-lg font-semibold text-gray-800">Forma de Entrega</h3>
      <div className="space-y-2">
        {tiposEntrega.map((opcao) => (
          <div className="flex items-center" key={opcao.tipo}>
            <input
              type="radio"
              id={opcao.tipo}
              name="delivery"
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              onChange={handleOptionChange}
              checked={selectedOption === opcao.tipo}
            />
            <label htmlFor={opcao.tipo} className="ml-2 capitalize text-gray-700">
              {formatarTipo(opcao.tipo)}
            </label>
          </div>
        ))}

        {selectedOption === "outros" && (
          <div className="mt-4">
            <label htmlFor="otherOption" className="block text-gray-700">
              {exibirOutrosSomenteLeitura
                ? "Forma de entrega informada pela expedição:"
                : "Especifique a forma de entrega:"}
            </label>
            {exibirOutrosSomenteLeitura ? (
              <p
                id="otherOption"
                className="mt-1 whitespace-pre-wrap rounded-md border border-gray-200 bg-white px-3 py-2 text-gray-700"
              >
                {textoOutrosExibido}
              </p>
            ) : (
              <input
                type="text"
                id="otherOption"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-700 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={outrosTexto ?? otherOption}
                onChange={handleOtherOptionChange}
                placeholder="Digite o tipo de entrega"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * EXPORTS
 */

export { DeliveryOptions };
